import { useState, useEffect, useRef } from 'react';
import { Play, Lock, Eye, Volume2, Video, Image as ImageIcon, Loader2, AlertCircle } from 'lucide-react';
import VoiceNotePlayer from './VoiceNotePlayer';

const MEDIA_PLACEHOLDER_HEIGHT = 200;
const MEDIA_PLACEHOLDER_WIDTH = 280;

const MediaMessage = ({ msg, isMine, onMediaPlayed, onOpenViewer }) => {
  const [hasError, setHasError] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const mediaRef = useRef(null);

  // Lazy loading with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { 
        rootMargin: '100px',
        threshold: 0.1 
      }
    );

    if (mediaRef.current) {
      observer.observe(mediaRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (msg.expires_at) {
      const expiryDate = new Date(msg.expires_at);
      if (new Date() > expiryDate) {
        setIsExpired(true);
      }
    }
  }, [msg.expires_at]);

  const handlePlay = () => {
    if (!isMine && !hasPlayed) {
      setHasPlayed(true);
      if (onMediaPlayed) {
        onMediaPlayed(msg.id);
      }
    }
  };

  if (isExpired) {
    return null;
  }

  if (msg.status === 'sending') {
    return (
      <div className="flex items-center gap-2 py-3 px-4 rounded-lg" style={{ background: 'rgba(124,58,237,0.15)' }}>
        <Loader2 className="h-4 w-4 text-purple-400 animate-spin" />
        <span className="text-xs text-purple-400">Uploading...</span>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex items-center gap-2 py-2 px-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.1)' }}>
        <AlertCircle className="h-4 w-4 text-red-400" />
        <span className="text-xs text-red-400">Media unavailable</span>
      </div>
    );
  }

  const mediaUrl = msg.media_urls?.[0];
  if (!mediaUrl) {
    if (msg.message_type === 'audio') {
      return (
        <div className="flex items-center gap-2 py-2 px-3 rounded-lg" style={{ background: 'rgba(124,58,237,0.1)' }}>
          <Volume2 className="h-4 w-4 text-purple-400" />
          <span className="text-xs text-purple-400">Voice message</span>
        </div>
      );
    }
    return null;
  }

  const authToken = localStorage.getItem('token');
  const addTokenToUrl = (url) => {
    if (!url) return null;
    const baseUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}token=${authToken}`;
  };

  const fullUrl = addTokenToUrl(mediaUrl);
  const thumbnailUrl = msg.thumbnail_url ? addTokenToUrl(msg.thumbnail_url) : null;
  
  const isViewOnce = msg.view_once && msg.message_type !== 'audio';
  const isListenOnce = msg.listen_once && msg.message_type === 'audio';

  const MediaPlaceholder = ({ type }) => (
    <div 
      className="rounded-2xl flex items-center justify-center animate-pulse"
      style={{ 
        width: type === 'audio' ? '200px' : `${MEDIA_PLACEHOLDER_WIDTH}px`, 
        height: type === 'audio' ? '44px' : `${MEDIA_PLACEHOLDER_HEIGHT}px`, 
        background: type === 'audio' ? 'rgba(30,41,59,0.6)' : 'rgba(124,58,237,0.1)' 
      }}
    >
      {type === 'image' && <ImageIcon className="h-8 w-8 text-purple-400/50" />}
      {type === 'video' && <Video className="h-8 w-8 text-purple-400/50" />}
      {type === 'audio' && (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-slate-600/50" />
          <div className="flex gap-px">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="w-[2px] h-4 rounded-full bg-slate-600/30" />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="mb-2 relative" ref={mediaRef}>
      {(isViewOnce || isListenOnce) && (
        <div className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-1 rounded-full text-xs" 
             style={{ background: 'rgba(0,0,0,0.6)' }}>
          {msg.view_once_opened ? (
            <>
              <Eye className="h-3 w-3 text-green-400" />
              <span className="text-green-300">Opened</span>
            </>
          ) : (
            <>
              <Lock className="h-3 w-3 text-orange-400" />
              <span className="text-orange-300">
                {isListenOnce ? 'Listen once' : 'View once'}
              </span>
            </>
          )}
        </div>
      )}
      
      {msg.message_type === 'image' && (
        isVisible ? (
          <img 
            src={fullUrl}
            alt="Shared"
            loading="lazy"
            className="rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            style={{ 
              maxHeight: '300px', 
              minHeight: `${MEDIA_PLACEHOLDER_HEIGHT}px`, 
              minWidth: `${MEDIA_PLACEHOLDER_WIDTH}px`, 
              objectFit: 'cover' 
            }}
            onClick={() => onOpenViewer && onOpenViewer('image', fullUrl)}
            onError={() => setHasError(true)}
          />
        ) : (
          <MediaPlaceholder type="image" />
        )
      )}
      {msg.message_type === 'video' && (
        isVisible ? (
          <div 
            className="relative cursor-pointer group"
            onClick={() => onOpenViewer && onOpenViewer('video', fullUrl)}
          >
            {thumbnailUrl ? (
              <img 
                src={thumbnailUrl}
                alt="Video thumbnail"
                className="rounded-lg"
                style={{ 
                  maxHeight: '300px', 
                  minHeight: `${MEDIA_PLACEHOLDER_HEIGHT}px`, 
                  minWidth: `${MEDIA_PLACEHOLDER_WIDTH}px`, 
                  objectFit: 'cover' 
                }}
              />
            ) : (
              <div 
                className="rounded-lg flex items-center justify-center"
                style={{ 
                  height: `${MEDIA_PLACEHOLDER_HEIGHT}px`, 
                  width: `${MEDIA_PLACEHOLDER_WIDTH}px`, 
                  background: 'rgba(124,58,237,0.2)' 
                }}
              >
                <Play className="h-12 w-12 text-purple-400" />
              </div>
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg group-hover:bg-black/40 transition-colors">
              <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                <Play className="h-6 w-6 text-gray-900 ml-1" fill="currentColor" />
              </div>
            </div>
          </div>
        ) : thumbnailUrl ? (
          <img 
            src={thumbnailUrl} 
            alt="Video" 
            className="rounded-lg opacity-70"
            style={{ 
              height: `${MEDIA_PLACEHOLDER_HEIGHT}px`, 
              width: `${MEDIA_PLACEHOLDER_WIDTH}px`, 
              objectFit: 'cover' 
            }}
          />
        ) : (
          <MediaPlaceholder type="video" />
        )
      )}
      {msg.message_type === 'audio' && (
        isVisible ? (
          <VoiceNotePlayer 
            src={fullUrl}
            duration={msg.duration}
            isListenOnce={isListenOnce}
            isMine={isMine}
            onPlay={handlePlay}
            onError={() => setHasError(true)}
          />
        ) : (
          <MediaPlaceholder type="audio" />
        )
      )}
    </div>
  );
};

export default MediaMessage;
export { MEDIA_PLACEHOLDER_HEIGHT, MEDIA_PLACEHOLDER_WIDTH };
